import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function CustomerPrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="max-w-4xl mx-auto px-6 py-12 bg-gray-50">

      {/* BACK BUTTON */}
      <button
        onClick={() => navigate("/policies")}
        className="flex items-center gap-2 text-orange-500 font-medium mb-6"
      >
        <ArrowLeft size={18} />
        Back to Policies
      </button>

      {/* TITLE */}
      <h1 className="text-3xl font-bold mb-2">
        Jopo Customer App - Privacy & Data Protection Policy
      </h1>

      <p className="text-gray-600 mb-6">
        Last Updated: 10/02/2026
      </p>

      <p className="text--700 mb-8">
        Jopo Delivery We respects your privacy and is committed to protecting your personal data
        in line with the Tanzania Personal Data Protection Act, 2022 and applicable regulations.
        This policy explains how we collect, use, store, and protect your information when you
        use the Jopo Customer App.
      </p>

      {/* 1 */}
      <h2 className="text-xl font-bold mt-6">1. Information We Collect</h2>

      <p className="text-gray-700 mt-2">
        We only collect information necessary to provide delivery services and improve the platform, including:
      </p>

      <h3 className="font-semibold mt-4">a) Personal Information</h3>
      <ul className="list-disc ml-6 text-gray-700 mt-2 space-y-1">
        <li>Full name.</li>
        <li>Phone number.</li>
        <li>Email address.</li>
        <li>Delivery addresses (pickup and drop-off)</li>
      </ul>

      <h3 className="font-semibold mt-4">b) Account & Usage Data</h3>
      <ul className="list-disc ml-6 text-gray-700 mt-2 space-y-1">
        <li>App activity and service usage</li>
        <li>Support requests and communication history</li>
      </ul>

      <h3 className="font-semibold mt-4">c) Location Data</h3>
      <ul className="list-disc ml-6 text-gray-700 mt-2 space-y-1">
        <li>Real-time or approximate location during delivery requests and tracking (only when the app is in use)</li>
      </ul>

      <h3 className="font-semibold mt-4">d) Payment Information</h3>
      <ul className="list-disc ml-6 text-gray-700 mt-2 space-y-1">
        <li>Transaction references and payment status</li>
      </ul>

      <p className="text-gray-700 mt-2">
        (Note: Jopo delivery does not store full card or mobile money credentials.
        Payments are processed through licensed payment partners.)
      </p>

      {/* 2 */}
      <h2 className="text-xl font-bold mt-6">2. How We Use Your Information</h2>

      <p className="text-gray-700 mt-2">
        Your data is used strictly for legitimate service purposes, including:
      </p>

      <ul className="list-disc ml-6 text-gray-700 mt-2 space-y-1">
        <li>Creating and managing your Jopo delivery account.</li>
        <li>Processing delivery requests and matching you with riders.</li>
        <li>Enabling real-time tracking and delivery confirmation.</li>
        <li>Processing payments and issuing receipts.</li>
        <li>Communicating service updates, delivery status, and support responses.</li>
        <li>Improving app performance, security, and user experience.</li>
        <li>Meeting legal and regulatory obligations.</li>
      </ul>

      {/* 3 */}
      <h2 className="text-xl font-bold mt-6">3. Legal Basis for Processing</h2>
      <ul className="list-disc ml-6 text-gray-700 mt-2 space-y-1">
        <li>Your consent when you register and use the app.</li>
        <li>Performance of a contract (to provide delivery services).</li>
        <li>Compliance with legal obligations under Tanzanian law.</li>
        <li>Legitimate business interests, provided they do not override your rights.</li>
      </ul>

      {/* 4 */}
      <h2 className="text-xl font-bold mt-6">4. Data Sharing & Third Parties</h2>
      <ul className="list-disc ml-6 text-gray-700 mt-2 space-y-1">
        <li>Delivery partners (riders): name, phone number, pickup/drop-off details.</li>
        <li>Payment service providers: transaction references and confirmations.</li>
        <li>Technology partners: hosting and system support providers.</li>
      </ul>

      <p className="text-gray-700 mt-2">
        We do not sell or trade your personal data to third parties.
      </p>

      {/* 5 */}
      <h2 className="text-xl font-bold mt-6">5. Data Storage & Security</h2>
      <ul className="list-disc ml-6 text-gray-700 mt-2 space-y-1">
        <li>Secure servers and encrypted connections.</li>
        <li>Access controls for authorized staff only.</li>
        <li>Regular system monitoring and updates.</li>
      </ul>

      <p className="text-gray-700 mt-2">
        Your data is stored only for as long as necessary to provide the service or meet legal requirements.
      </p>

      {/* 6 */}
      <h2 className="text-xl font-bold mt-6">6. Your Rights as a User</h2>
      <ul className="list-disc ml-6 text-gray-700 mt-2 space-y-1">
        <li>Access your personal data.</li>
        <li>Request correction of inaccurate information.</li>
        <li>Request deletion of your data where legally permissible.</li>
        <li>Withdraw consent for non-essential processing.</li>
        <li>Object to certain uses of your data.</li>
      </ul>

      <p className="text-gray-700 mt-2">
        You can exercise these rights by contacting us using the details below.
      </p>

      {/* 7 */}
      <h2 className="text-xl font-bold mt-6">7. Data Retention</h2>

      <p className="text-gray-700 mt-2">
        We retain personal data only for as long as needed to:
      </p>

      <ul className="list-disc ml-6 text-gray-700 mt-2 space-y-1">
        <li>Provide our services.</li>
        <li>Resolve disputes.</li>
        <li>Comply with legal and regulatory obligations.</li>
      </ul>

      <p className="text-gray-700 mt-2">
        When data is no longer required, it is securely deleted or anonymized.
      </p>

      {/* 8 */}
      <h2 className="text-xl font-bold mt-6">8. Children’s Privacy</h2>
      <p className="text-gray-700 mt-2">
        The Jopo Customer App is not intended for users under the age of 18.
        We do not knowingly collect personal data from minors.
      </p>

      {/* 9 */}
      <h2 className="text-xl font-bold mt-6">9. Cross-Border Data Transfers</h2>
      <p className="text-gray-700 mt-2">
        Where data is processed or stored outside Tanzania, we ensure appropriate safeguards
        are in place to protect your information in line with applicable data protection standards.
      </p>

      {/* 10 */}
      <h2 className="text-xl font-bold mt-6">10. Changes to This Policy</h2>
      <p className="text-gray-700 mt-2">
        We may update this policy from time to time. Any material changes will be communicated
        through the app or our official channels.
      </p>

      {/* 11 */}
      <h2 className="text-xl font-bold mt-6">11. Contact Information</h2>

      <div className="text-gray-700 mt-2">
        <p>Data Protection Officer (DPO)</p>
        <p>Jopo</p>
        <p>Email: support@jopodelivery.com</p>
        <p>Phone: +255689966875</p>
      </div>
    </div>
  );
}